

import React, { useMemo, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { DailyActivityWithAssignments, ProjectWithZones, Craftsman, CraftsmanAssignment, TruckAssignment, ProjectZone } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { CalendarDays, Users, Truck, MapPin, Clock, ChevronLeft, ChevronRight, Car } from 'lucide-react';
import { format, parseISO, isValid, isAfter, startOfDay, isSameDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import { getBatchedDailyActivitiesForUserSchedule } from '@/features/projects/api';
import { Skeleton } from '@/shared/ui/skeleton';
import { Button } from '@/shared/ui/button';

/**
 * ユーザー割り当てスケジュールのプロパティ
 */
interface UserAssignmentScheduleProps {
    currentUserCraftsmanId?: string | null;
    craftsmen?: Craftsman[] | null;
}

/**
 * 外部から呼び出し可能な関数
 */
export interface UserAssignmentScheduleRef {
    refreshSchedule: () => Promise<void>;
}

/**
 * 車両グループの情報
 */
interface VehicleGroup {
    vehicleName: string | null;
    craftsmen: string[];
}

/**
 * トラック情報
 */
interface TruckInfo {
    truckName: string;
    driverName: string | null;
    notes?: string;
}

/**
 * 割り当て情報のインターフェース
 */
interface AssignmentInfo {
    date: string;
    projectName: string;
    projectId: string;
    zoneName: string | null;
    activity: DailyActivityWithAssignments;
    otherCraftsmen: string[];
    trucksCount: number;
    hasVehicle: boolean;
    vehicleName?: string;
    // 詳細な情報を追加
    vehicleGroups: VehicleGroup[];
    trucks: TruckInfo[];
    userVehicleName?: string;
}

/**
 * ユーザー割り当てスケジュール
 * @param param0 ユーザー割り当てスケジュールのプロパティ
 * @returns ユーザー割り当てスケジュール
 */
export const UserAssignmentSchedule = React.memo(forwardRef<UserAssignmentScheduleRef, UserAssignmentScheduleProps>(({
    currentUserCraftsmanId,
    craftsmen
}, ref) => {
    const [allActivities, setAllActivities] = useState<Map<string, DailyActivityWithAssignments[]>>(new Map());
    const [projectsData, setProjectsData] = useState<Map<string, ProjectWithZones>>(new Map());
    const [monthlyCache, setMonthlyCache] = useState<Map<string, Map<string, DailyActivityWithAssignments[]>>>(new Map());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() + 1 };
    });


    // 月単位の日次活動を取得（キャッシュ機能付き）
    useEffect(() => {
        const fetchAllActivities = async () => {
            if (!currentUserCraftsmanId) {
                setIsLoading(false);
                return;
            }

            // キャッシュキーを生成
            const monthKey = `${currentMonth.year}-${String(currentMonth.month).padStart(2, '0')}`;

            // キャッシュからデータを確認
            const cachedData = monthlyCache.get(monthKey);
            if (cachedData) {
                setAllActivities(cachedData);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                // 現在選択されている月の開始日と終了日を計算
                const startOfMonth = new Date(currentMonth.year, currentMonth.month - 1, 1);
                const endOfMonth = new Date(currentMonth.year, currentMonth.month, 0);
                const startDateStr = startOfMonth.toISOString().split('T')[0];
                const endDateStr = endOfMonth.toISOString().split('T')[0];

                // まずプロジェクト情報を取得
                const apiClient = (await import('@/shared/api/client')).default;
                const projectsResponse = await apiClient.get('/projects', {
                    params: {
                        status: '進行中',
                        include_zones: true,
                        limit: 500   // API制限に準拠
                    }
                });

                const allProjects = projectsResponse.data.items || [];
                const allProjectIds = allProjects.map((p: ProjectWithZones) => p.id);

                // プロジェクトデータをMapに変換
                const projectsMap = new Map<string, ProjectWithZones>();
                allProjects.forEach((project: ProjectWithZones) => {
                    projectsMap.set(project.id, project);
                });
                setProjectsData(projectsMap);

                // 次にスケジュール情報を取得
                const activitiesResult = await getBatchedDailyActivitiesForUserSchedule(
                    allProjectIds,
                    {
                        startDate: startDateStr,
                        endDate: endDateStr
                    }
                );

                // エラーハンドリング
                const { data: activitiesMap, errors } = activitiesResult;
                if (errors.length > 0) {
                    console.warn('Some projects failed to load:', errors);
                    if (errors.length === allProjectIds.length) {
                        setError('スケジュールデータの取得に失敗しました');
                    }
                }

                // データをキャッシュに保存
                setMonthlyCache(prev => {
                    const newCache = new Map(prev);
                    newCache.set(monthKey, activitiesMap);
                    // キャッシュサイズを制限（最大6ヶ月分）
                    if (newCache.size > 6) {
                        const oldestKey = newCache.keys().next().value;
                        if (oldestKey) {
                            newCache.delete(oldestKey);
                        }
                    }
                    return newCache;
                });
                setAllActivities(activitiesMap);
            } catch (error) {
                console.error('Failed to fetch activities:', error);
                setError('スケジュールデータの取得に失敗しました');
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllActivities();
    }, [currentUserCraftsmanId, currentMonth, monthlyCache]);

    // 現在のユーザーの職人名を取得
    const currentUserCraftsmanName = useMemo(() => {
        if (!currentUserCraftsmanId || !craftsmen) return '職人';
        const currentCraftsman = craftsmen.find(c => c.id === currentUserCraftsmanId);
        return currentCraftsman?.name || '職人';
    }, [currentUserCraftsmanId, craftsmen]);

    // 日付文字列の正規化
    const normalizeDateString = (dateValue: string | Date | null | undefined): string | null => {
        if (!dateValue) return null;

        if (typeof dateValue === 'string') {
            if (dateValue.includes('T')) {
                return dateValue.split('T')[0];
            } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            } else {
                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                    return date.toISOString().split('T')[0];
                }
            }
        }
        return null;
    };


    // プロジェクトマップは状態として管理（projectsDataを使用）

    // 現在の月でフィルタされたユーザーアクティビティ（最適化版）
    const filteredUserActivities = useMemo(() => {
        if (!currentUserCraftsmanId) return [];

        const activities: Array<{
            activity: DailyActivityWithAssignments;
            project: ProjectWithZones;
            dateStr: string;
            userAssignment: CraftsmanAssignment;
        }> = [];

        // allActivitiesをイテレートして効率化
        for (const [projectId, projectActivities] of allActivities.entries()) {
            const project = projectsData.get(projectId);
            if (!project) continue;

            for (const activity of projectActivities) {
                const userAssignment = activity.craftsman_assignments?.find(
                    assignment => assignment.craftsman_id === currentUserCraftsmanId
                );

                if (userAssignment) {
                    const dateValue = activity.schedule_date;
                    const dateStr = normalizeDateString(dateValue);

                    if (dateStr) {
                        // 現在選択されている月のデータのみフィルタ（パフォーマンス改善）
                        const dateObj = new Date(dateStr);
                        const isInCurrentMonth = dateObj.getFullYear() === currentMonth.year &&
                            dateObj.getMonth() + 1 === currentMonth.month;
                        if (isInCurrentMonth) {
                            activities.push({
                                activity,
                                project,
                                dateStr,
                                userAssignment
                            });
                        }
                    }
                }
            }
        }

        return activities;
    }, [projectsData, allActivities, currentUserCraftsmanId, currentMonth]);

    // craftsmen名前マップ（最適化）
    const craftsmenNameMap = useMemo(() => {
        const map = new Map<string, string>();
        if (craftsmen) {
            craftsmen.forEach(craftsman => {
                map.set(craftsman.id, craftsman.name);
            });
        }
        return map;
    }, [craftsmen]);

    // 職人名取得の最適化関数
    const getCraftsmanNameOptimized = useCallback((assignment: CraftsmanAssignment): string => {
        return assignment.craftsman_name ||
            (assignment.craftsman_id ? craftsmenNameMap.get(assignment.craftsman_id) : null) ||
            '不明な職人';
    }, [craftsmenNameMap]);

    // フィルタされたアクティビティから最終的なアサインメント情報を生成（最適化版）
    const userAssignments = useMemo(() => {
        return filteredUserActivities.map(({ activity, project, dateStr, userAssignment }) => {
            // 工区名を取得
            const zone = project.project_zones?.find((z: ProjectZone) => z.id === activity.zone_id);
            const zoneName = zone?.zone_name || null;

            // 他の職人を取得
            const otherCraftsmen = activity.craftsman_assignments
                ?.filter((assignment: CraftsmanAssignment) => assignment.craftsman_id !== currentUserCraftsmanId)
                .map(getCraftsmanNameOptimized) || [];

            // トラック数
            const trucksCount = activity.truck_assignments?.length || 0;

            // 社用車情報
            const vehicleName = userAssignment.company_vehicle_name ||
                userAssignment.company_vehicle?.name ||
                (userAssignment.vehicle_id ? `車両ID: ${userAssignment.vehicle_id}` : undefined);

            const hasVehicle = !!(vehicleName || userAssignment.vehicle_id || userAssignment.company_vehicle);
            const userVehicleName = vehicleName;

            // 車両ごとのグループを作成
            const vehicleMap = new Map<string, string[]>();
            activity.craftsman_assignments?.forEach((assignment: CraftsmanAssignment) => {
                if (assignment.craftsman_id === currentUserCraftsmanId) return; // 自分は除外

                const assignmentVehicleName = assignment.company_vehicle_name ||
                    assignment.company_vehicle?.name ||
                    (assignment.vehicle_id ? `車両ID: ${assignment.vehicle_id}` : null);

                const craftsmanName = getCraftsmanNameOptimized(assignment);
                const vehicleKey = assignmentVehicleName || 'no-vehicle';

                if (!vehicleMap.has(vehicleKey)) {
                    vehicleMap.set(vehicleKey, []);
                }
                vehicleMap.get(vehicleKey)!.push(craftsmanName);
            });

            const vehicleGroups: VehicleGroup[] = [];
            vehicleMap.forEach((craftsmen, vehicleKey) => {
                vehicleGroups.push({
                    vehicleName: vehicleKey === 'no-vehicle' ? null : vehicleKey,
                    craftsmen
                });
            });

            // トラック情報を取得
            const trucks: TruckInfo[] = activity.truck_assignments?.map((assignment: TruckAssignment) => ({
                truckName: (assignment as any).license_plate || (assignment.truck as any)?.name || '不明なトラック',
                driverName: (assignment as any).truck_driver_name || (assignment.truck_driver as any)?.name || null,
                notes: (assignment as any).note
            })) || [];

            return {
                date: dateStr,
                projectName: project.name,
                projectId: project.id,
                zoneName,
                activity,
                otherCraftsmen,
                trucksCount,
                hasVehicle,
                vehicleName,
                vehicleGroups,
                trucks,
                userVehicleName
            } satisfies AssignmentInfo;
        }).sort((a, b) => a.date.localeCompare(b.date));
    }, [filteredUserActivities, currentUserCraftsmanId, getCraftsmanNameOptimized]);

    // 日付をフォーマット
    const formatDate = (dateStr: string) => {
        try {
            const date = parseISO(dateStr);
            if (isValid(date)) {
                return format(date, 'M月d日(E)', { locale: ja });
            }
        } catch (error) {
            console.error('日付フォーマットエラー:', error);
        }
        return dateStr;
    };

    // 月ナビゲーション関数
    const goToPreviousMonth = () => {
        setCurrentMonth(prev => {
            if (prev.month === 1) {
                return { year: prev.year - 1, month: 12 };
            } else {
                return { year: prev.year, month: prev.month - 1 };
            }
        });
    };

    const goToNextMonth = () => {
        setCurrentMonth(prev => {
            if (prev.month === 12) {
                return { year: prev.year + 1, month: 1 };
            } else {
                return { year: prev.year, month: prev.month + 1 };
            }
        });
    };


    // 現在の月の表示用文字列
    const currentMonthDisplay = `${currentMonth.year}年${currentMonth.month}月`;

    // 現在の月のアサインメントを表示（本日以降の日付のみ）
    const upcomingAssignments = useMemo(() => {
        const today = startOfDay(new Date());
        return userAssignments.filter((assignment) => {
            const date = parseISO(assignment.date);
            return isSameDay(date, today) || isAfter(date, today);
        });
    }, [userAssignments]);

    // 手動更新機能
    const refreshSchedule = useCallback(async () => {
        if (!currentUserCraftsmanId) return;

        // キャッシュをクリア
        setMonthlyCache(new Map());
        setIsLoading(true);
        setError(null);

        try {
            // 現在選択されている月の開始日と終了日を計算
            const startOfMonth = new Date(currentMonth.year, currentMonth.month - 1, 1);
            const endOfMonth = new Date(currentMonth.year, currentMonth.month, 0);
            const startDateStr = startOfMonth.toISOString().split('T')[0];
            const endDateStr = endOfMonth.toISOString().split('T')[0];

            // プロジェクト情報を再取得
            const apiClient = (await import('@/shared/api/client')).default;
            const projectsResponse = await apiClient.get('/projects', {
                params: {
                    status: '進行中',
                    include_zones: true,
                    limit: 500
                }
            });

            const allProjects = projectsResponse.data.items || [];
            const allProjectIds = allProjects.map((p: ProjectWithZones) => p.id);

            const projectsMap = new Map<string, ProjectWithZones>();
            allProjects.forEach((project: ProjectWithZones) => {
                projectsMap.set(project.id, project);
            });
            setProjectsData(projectsMap);

            // スケジュール情報を再取得
            const activitiesResult = await getBatchedDailyActivitiesForUserSchedule(
                allProjectIds,
                {
                    startDate: startDateStr,
                    endDate: endDateStr
                }
            );

            const { data: activitiesMap, errors } = activitiesResult;
            if (errors.length > 0) {
                console.warn('Some projects failed to load:', errors);
                if (errors.length === allProjectIds.length) {
                    setError('スケジュールデータの取得に失敗しました');
                }
            }

            // データを更新
            setAllActivities(activitiesMap);

            // キャッシュも更新
            const monthKey = `${currentMonth.year}-${String(currentMonth.month).padStart(2, '0')}`;
            setMonthlyCache(prev => {
                const newCache = new Map(prev);
                newCache.set(monthKey, activitiesMap);
                return newCache;
            });

        } catch (error) {
            console.error('Failed to refresh schedule:', error);
            setError('スケジュールデータの更新に失敗しました');
        } finally {
            setIsLoading(false);
        }
    }, [currentUserCraftsmanId, currentMonth]);

    // ref経由で外部から呼び出し可能にする
    useImperativeHandle(ref, () => ({
        refreshSchedule
    }), [refreshSchedule]);

    if (!currentUserCraftsmanId) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <CalendarDays className="h-5 w-5" />
                        {currentUserCraftsmanName}のスケジュール
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-muted-foreground">
                        職人として登録されていないため、スケジュールを表示できません。
                    </p>
                </CardContent>
            </Card>
        );
    }

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <CalendarDays className="h-5 w-5" />
                        {currentUserCraftsmanName}のスケジュール
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <CalendarDays className="h-5 w-5" />
                        {currentUserCraftsmanName}のスケジュール
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-destructive">{error}</p>
                </CardContent>
            </Card>
        );
    }

    if (upcomingAssignments.length === 0) {
        return (
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between fluid-sm md:text-lg">
                        <div className="flex items-center gap-2">
                            <CalendarDays className="h-5 w-5" />
                            {currentUserCraftsmanName}のスケジュール
                            <Badge variant="secondary" className="ml-2">
                                0件
                            </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={goToPreviousMonth}>
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm font-medium text-muted-foreground min-w-[100px] text-center">
                                {currentMonthDisplay}
                            </span>
                            <Button variant="outline" size="sm" onClick={goToNextMonth}>
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <div className="text-center py-8">
                        <CalendarDays className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground mb-2">
                            {currentMonthDisplay}にアサインされているスケジュールはありません
                        </p>
                        <p className="text-sm text-muted-foreground">
                            他の月を確認するには上部の矢印ボタンをご利用ください
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between fluid-sm md:text-lg">
                    <div className="flex items-center gap-2">
                        <CalendarDays className="h-5 w-5" />
                        {currentUserCraftsmanName}のスケジュール
                        <Badge variant="secondary" className="ml-2">
                            {upcomingAssignments.length}件
                        </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={goToPreviousMonth}>
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm font-medium text-muted-foreground min-w-[100px] text-center">
                            {currentMonthDisplay}
                        </span>
                        <Button variant="outline" size="sm" onClick={goToNextMonth}>
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
                <div className="max-h-[600px] overflow-y-auto space-y-3">
                    {upcomingAssignments.map((assignment) => (
                        <div
                            key={`${assignment.projectId}-${assignment.date}-${assignment.activity.id}`}
                            className="border rounded-lg p-3 bg-gradient-to-r from-green-50/50 to-emerald-50/70 dark:from-emerald-950/10 dark:to-emerald-900/10 border-emerald-100/70 dark:border-emerald-800/30 hover:shadow-md transition-shadow"
                        >
                            {/* ヘッダー部分 - 日付とプロジェクト名 */}
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                                    <span className="font-semibold text-emerald-700 dark:text-emerald-300 text-sm">
                                        {formatDate(assignment.date)}
                                    </span>
                                </div>
                                {assignment.zoneName && (
                                    <Badge variant="secondary" className="text-xs">
                                        <MapPin className="h-3 w-3 mr-1" />
                                        {assignment.zoneName}
                                    </Badge>
                                )}
                            </div>

                            {/* プロジェクト名 */}
                            <h3 className="font-medium fluid-sm md:text-lg mb-2 text-gray-900 dark:text-gray-100">
                                {assignment.projectName}
                            </h3>

                            {/* メモ表示 */}
                            {assignment.activity.notes && (
                                <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-950/30 rounded border-l-2 border-blue-400">
                                    <p className="text-xs text-blue-800 dark:text-blue-200">{assignment.activity.notes}</p>
                                </div>
                            )}

                            {/* 職人の車両情報（最重要） */}
                            <div className="mb-3 p-2 rounded-md border">
                                {assignment.hasVehicle ? (
                                    <div className="bg-green-50 dark:bg-green-950/50 border-green-200 dark:border-green-800 rounded-md p-2">
                                        <div className="flex items-center gap-2">
                                            <Car className="h-4 w-4 text-green-600 dark:text-green-400" />
                                            <span className="font-medium text-green-800 dark:text-green-200">{currentUserCraftsmanName}の車両:</span>
                                            <span className="text-green-700 dark:text-green-300 font-semibold">
                                                {assignment.userVehicleName || '社用車'}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-800 rounded-md p-2">
                                        <div className="flex items-center gap-2">
                                            <Car className="h-4 w-4 text-gray-500" />
                                            <span className="font-medium text-gray-600 dark:text-gray-400">{currentUserCraftsmanName}: 車両なし</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* その他リソース情報のサマリー */}
                            <div className="flex flex-wrap gap-1.5">
                                {/* 他の職人 */}
                                {assignment.otherCraftsmen.length > 0 && (
                                    <Badge variant="outline" className="bg-gray-50 dark:bg-gray-900/30 text-xs">
                                        <Users className="h-3 w-3 mr-1" />
                                        他{assignment.otherCraftsmen.length}名
                                    </Badge>
                                )}

                                {/* トラック */}
                                {assignment.trucksCount > 0 && (
                                    <Badge variant="outline" className="bg-gray-50 dark:bg-gray-900/30 text-xs">
                                        <Truck className="h-3 w-3 mr-1" />
                                        トラック{assignment.trucksCount}台
                                    </Badge>
                                )}
                            </div>

                            {/* その他リソース詳細 */}
                            {(assignment.vehicleGroups.length > 0 || assignment.trucks.length > 0) && (
                                <div className="mt-3 pt-3 border-t border-emerald-100 dark:border-emerald-800/30">
                                    <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                                        <span>その他リソース</span>
                                        <Badge variant="secondary" className="text-[10px] h-4">
                                            参考情報
                                        </Badge>
                                    </h4>

                                    <div className="space-y-2">
                                        {/* 車両ごとの職人グループ */}
                                        {assignment.vehicleGroups.map((vehicleGroup, groupIndex) => (
                                            <div key={groupIndex} className="text-xs bg-gray-50/50 dark:bg-gray-900/20 rounded p-2">
                                                <div className="flex items-start gap-1">
                                                    <Car className="h-3 w-3 mt-0.5 text-gray-500 flex-shrink-0" />
                                                    <div>
                                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                                            {vehicleGroup.vehicleName || '車両なし'}:
                                                        </span>
                                                        <span className="text-gray-600 dark:text-gray-400 ml-1">
                                                            {vehicleGroup.craftsmen.join(', ')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {/* トラック詳細 */}
                                        {assignment.trucks.map((truck, truckIndex) => (
                                            <div key={truckIndex} className="text-xs bg-gray-50/50 dark:bg-gray-900/20 rounded p-2">
                                                <div className="flex items-start gap-1">
                                                    <Truck className="h-3 w-3 mt-0.5 text-gray-500 flex-shrink-0" />
                                                    <div>
                                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                                            {truck.truckName}:
                                                        </span>
                                                        <span className="text-gray-600 dark:text-gray-400 ml-1">
                                                            {truck.driverName ? `運転手: ${truck.driverName}` : '運転手なし'}
                                                        </span>
                                                        {truck.notes && (
                                                            <div className="text-gray-500 dark:text-gray-500 text-xs mt-1 ml-4">
                                                                メモ: {truck.notes}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* スクロール可能であることを示すヒント */}
                {upcomingAssignments.length > 3 && (
                    <div className="text-center pt-2 border-t">
                        <p className="text-xs text-muted-foreground">
                            ↕ スクロールして他のスケジュールを確認
                        </p>
                    </div>
                )}
            </CardContent>
        </Card >
    );
}));
